import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, Mail, Search, Plus } from "lucide-react";
import api from "./api";

/**
 * /console/messages , Heritage Chat admin inbox.
 *
 * Lists every customer thread on the left, the chat view on the right.
 * Replies are sent via POST /admin/customer-messages/reply and also email
 * the customer (existing backend behaviour). Unread counts from
 * GET /admin/customer-messages/threads.
 */
export default function ConsoleMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // customer_email
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [threadFilter, setThreadFilter] = useState("all"); // all | unread | priority | answered
  const [sortBy, setSortBy] = useState("latest");           // latest | oldest | unread_desc | name
  const [priority, setPriority] = useState("normal");
  const [orderId, setOrderId] = useState("");
  const [custOrders, setCustOrders] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeEmail, setComposeEmail] = useState("");
  const endRef = useRef(null);

  const loadThreads = async () => {
    setLoadingThreads(true);
    try {
      const r = await api.get("/admin/customer-messages/threads");
      setThreads(r.data || []);
      if (!active && r.data?.length) setActive(r.data[0].customer_email);
    } catch (e) {
      toast.error("Couldn't load message threads.");
    } finally {
      setLoadingThreads(false);
    }
  };

  const loadThread = async (email) => {
    if (!email) return;
    setLoadingThread(true);
    try {
      const r = await api.get(`/admin/customer-messages/${encodeURIComponent(email)}`);
      setMessages(r.data || []);
      window.dispatchEvent(new Event("comms-read"));
    } catch (e) {
      toast.error("Couldn't load thread.");
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/customer-messages/threads");
        if (cancelled) return;
        setThreads(r.data || []);
        if (!active && r.data?.length) setActive(r.data[0].customer_email);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load message threads.");
      } finally {
        if (!cancelled) setLoadingThreads(false);
      }
    })();
    return () => { cancelled = true; };
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setOrderId("");
    api.get("/console/orders", { params: { stage: "all", q: active } })
      .then((r) => { if (!cancelled) setCustOrders(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    (async () => {
      if (!cancelled) setLoadingThread(true);
      try {
        const r = await api.get(`/admin/customer-messages/${encodeURIComponent(active)}`);
        if (!cancelled) setMessages(r.data || []);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load thread.");
      } finally {
        if (!cancelled) setLoadingThread(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const reply = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      await api.post("/admin/customer-messages/reply", {
        customer_email: active,
        body: body.trim(),
        priority,
        order_id: orderId || null,
      });
      setBody(""); setPriority("normal"); setOrderId("");
      await loadThread(active);
      await loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  const composeNew = async () => {
    const em = composeEmail.trim().toLowerCase();
    if (!em || !em.includes("@")) { toast.error("Enter the customer's email."); return; }
    setActive(em);
    setComposeOpen(false);
    setComposeEmail("");
    toast.info(`Thread opened for ${em}, write your message below.`);
  };

  const visibleThreads = useMemo(() => {
    let xs = threads;
    const needle = q.trim().toLowerCase();
    if (needle) {
      xs = xs.filter((t) =>
        (t.customer_email || "").toLowerCase().includes(needle) ||
        (t.customer_name || "").toLowerCase().includes(needle) ||
        (t.latest_body || "").toLowerCase().includes(needle),
      );
    }
    if (threadFilter === "unread")   xs = xs.filter((t) => (t.unread_admin || 0) > 0);
    if (threadFilter === "priority") xs = xs.filter((t) => t.top_priority === "urgent" || t.top_priority === "high");
    if (threadFilter === "answered") xs = xs.filter((t) => t.latest_sender === "admin");
    const prank = (t) => ({ urgent: 2, high: 1 }[t.top_priority] || 0);
    const cmp = {
      latest:      (a, b) => prank(b) - prank(a) || (b.latest_at || "").localeCompare(a.latest_at || ""),
      oldest:      (a, b) => (a.latest_at || "").localeCompare(b.latest_at || ""),
      unread_desc: (a, b) => (b.unread_admin || 0) - (a.unread_admin || 0) || (b.latest_at || "").localeCompare(a.latest_at || ""),
      name:        (a, b) => (a.customer_name || a.customer_email || "").localeCompare(b.customer_name || b.customer_email || ""),
    }[sortBy];
    return [...xs].sort(cmp || (() => 0));
  }, [threads, q, threadFilter, sortBy]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-180px)] min-h-[480px]" data-testid="console-messages">
      {/* Threads sidebar */}
      <aside className="bg-white/95 border border-[#E8DFCB] rounded overflow-hidden flex flex-col">
        <header className="px-4 py-3 bg-[#6E1E3A] text-white">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-lg flex items-center gap-2">
              <Mail className="w-4 h-4" /> Communications
            </div>
            <button type="button" onClick={() => setComposeOpen((v) => !v)} data-testid="messages-new"
              className="px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 text-[9px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> New
            </button>
          </div>
          <div className="text-[10px] tracking-[0.25em] uppercase opacity-80 mt-0.5">
            {visibleThreads.length} of {threads.length} thread{threads.length === 1 ? "" : "s"}
          </div>
        </header>
        {composeOpen && (
          <div className="p-2.5 border-b border-[#E8DFCB] bg-white space-y-1.5" data-testid="messages-compose">
            <input
              type="email" placeholder="customer@email.nz"
              value={composeEmail} onChange={(e) => setComposeEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && composeNew()}
              data-testid="compose-email"
              className="w-full px-2.5 py-1.5 text-xs border border-[#E8DFCB] rounded outline-none"
            />
            <button type="button" onClick={composeNew} data-testid="compose-open"
              className="w-full py-1.5 rounded bg-[#6E1E3A] text-white text-[10px] tracking-[0.2em] uppercase font-semibold">
              Open thread
            </button>
          </div>
        )}
        <div className="p-2 border-b border-[#E8DFCB] bg-[#FBF6E9] space-y-1.5">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-[#E8DFCB] rounded">
            <Search className="w-3.5 h-3.5 text-[#9C8B73]" />
            <input
              type="text" placeholder="Search threads…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="messages-search"
              className="flex-1 outline-none text-xs bg-transparent"
            />
          </div>
          <div className="flex items-center gap-1">
            {[
              { v: "all",      l: "All" },
              { v: "unread",   l: "Unread" },
              { v: "priority", l: "Priority" },
              { v: "answered", l: "Done" },
            ].map((opt) => (
              <button
                key={opt.v} type="button"
                onClick={() => setThreadFilter(opt.v)}
                data-testid={`messages-filter-${opt.v}`}
                className={`flex-1 px-2 py-1 text-[9px] tracking-[0.25em] uppercase font-semibold rounded ${
                  threadFilter === opt.v ? "bg-[#6E1E3A] text-white" : "bg-white border border-[#E8DFCB] text-[#3C2A1E] hover:bg-[#FBF6E9]"
                }`}
              >
                {opt.l}
              </button>
            ))}
            <select
              value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              data-testid="messages-sort"
              className="text-[10px] bg-white border border-[#E8DFCB] rounded px-1.5 py-1 outline-none"
            >
              <option value="latest">Latest</option>
              <option value="oldest">Oldest</option>
              <option value="unread_desc">Unread first</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-auto divide-y divide-[#F2E8D6]" data-testid="message-threads">
          {loadingThreads ? (
            <div className="p-6 grid place-items-center text-[#9C8B73]"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : visibleThreads.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#9C8B73]">{threads.length === 0 ? "No messages yet." : "No threads match."}</div>
          ) : visibleThreads.map((t) => (
            <button
              key={t.customer_email}
              type="button"
              onClick={() => setActive(t.customer_email)}
              data-testid={`thread-${t.customer_email}`}
              className={`w-full text-left px-4 py-3 transition ${active === t.customer_email ? "bg-[#FBF6E9]" : "hover:bg-[#FBF6E9]/60"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-base text-[#3C2A1E] truncate">
                  {t.customer_name || t.customer_email}
                </div>
                <span className="flex items-center gap-1">
                  {t.top_priority === "urgent" && (
                    <span data-testid={`thread-urgent-${t.customer_email}`} className="px-1.5 py-0.5 text-[8px] tracking-[0.15em] uppercase bg-red-700 text-white rounded-full font-bold">Urgent</span>
                  )}
                  {t.top_priority === "high" && (
                    <span data-testid={`thread-high-${t.customer_email}`} className="px-1.5 py-0.5 text-[8px] tracking-[0.15em] uppercase bg-amber-500 text-white rounded-full font-bold">Important</span>
                  )}
                  {t.unread_admin > 0 && (
                    <span data-testid={`thread-unread-${t.customer_email}`} className="px-1.5 py-0.5 text-[9px] tracking-[0.2em] uppercase bg-[#A33030] text-white rounded font-semibold">
                      {t.unread_admin}
                    </span>
                  )}
                </span>
              </div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#9C8B73] mt-0.5 truncate">
                {t.latest_sender === "admin" ? "You: " : ""}{t.latest_body}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-1">
                {new Date(t.latest_at).toLocaleString("en-NZ", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat view */}
      <section className="bg-white/95 border border-[#E8DFCB] rounded overflow-hidden flex flex-col">
        {active ? (
          <>
            <header className="px-4 py-3 bg-[#FBF6E9] border-b border-[#E8DFCB]">
              <div className="font-display text-lg text-[#3C2A1E] flex items-center gap-2" data-testid="chat-header">
                <MessageCircle className="w-4 h-4 text-[#6E1E3A]" /> {active}
              </div>
            </header>
            <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="chat-messages">
              {loadingThread ? (
                <div className="grid place-items-center py-12 text-[#9C8B73]"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12 text-sm text-[#9C8B73]">No messages in this thread yet.</div>
              ) : messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`msg-${m.id}`}
                  className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[80%] px-3 py-2 rounded-lg ${m.sender === "admin" ? "bg-[#6E1E3A] text-white" : "bg-[#FBF6E9] border border-[#E8DFCB] text-[#3C2A1E]"}`}>
                    <div className="flex flex-wrap gap-1.5 mb-1 empty:hidden">
                      {m.priority === "urgent" && <span className="px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.15em] uppercase font-bold bg-red-700 text-white">Urgent</span>}
                      {m.priority === "high" && <span className="px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.15em] uppercase font-bold bg-amber-500 text-white">Important</span>}
                      {m.order_id && (
                        <Link to={`/admin/order/${m.order_id}`} data-testid={`msg-order-${m.id}`}
                          className={`px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.15em] uppercase font-semibold underline ${m.sender === "admin" ? "bg-white/20 text-white" : "bg-black/10 text-[#3C2A1E]"}`}>
                          Order #{String(m.order_id).slice(0, 8)}
                        </Link>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                    <div className={`text-[9px] tracking-[0.2em] uppercase mt-1 ${m.sender === "admin" ? "text-white/70" : "text-[#9C8B73]"}`}>
                      {m.sender === "admin" ? "You" : (m.customer_name || "Customer")}
                      {" · "}
                      {new Date(m.created_at).toLocaleString("en-NZ", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                      {m.sender === "admin" && (m.read_by_customer ? " · read" : " · sent")}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={reply} className="p-3 border-t border-[#E8DFCB] bg-[#FBF6E9] space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {[["normal", "General"], ["high", "Important"], ["urgent", "Urgent"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPriority(v)} data-testid={`reply-priority-${v}`}
                    className={`px-2.5 py-1 rounded-full border text-[9px] tracking-[0.15em] uppercase font-semibold ${
                      priority === v
                        ? (v === "urgent" ? "bg-red-700 border-red-700 text-white" : v === "high" ? "bg-amber-500 border-amber-500 text-white" : "bg-[#6E1E3A] border-[#6E1E3A] text-white")
                        : "border-[#E8DFCB] text-[#3C2A1E] bg-white"
                    }`}>
                    {l}
                  </button>
                ))}
                {custOrders.length > 0 && (
                  <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="reply-order"
                    className="text-[11px] bg-white border border-[#E8DFCB] rounded px-1.5 py-1 outline-none max-w-[220px]">
                    <option value="">Not about an order</option>
                    {custOrders.map((o) => (
                      <option key={o.id} value={o.id}>
                        Link {o.order_number ? `#${o.order_number}` : `#${String(o.id).slice(0, 8)}`} · {(o.stage || "").replace("_", " ")} · ${Number(o.total || 0).toFixed(0)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex items-start gap-2">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={2}
                  placeholder={`Reply to ${active}…`}
                  data-testid="reply-input"
                  className="flex-1 px-3 py-2 text-sm bg-white border border-[#E8DFCB] rounded resize-none focus:outline-none focus:border-[#6E1E3A]"
                />
                <button
                  type="submit"
                  disabled={!body.trim() || sending}
                  data-testid="reply-send"
                  className="px-4 h-12 inline-flex items-center gap-2 bg-[#6E1E3A] hover:bg-[#A33030] text-white text-[10px] tracking-[0.25em] uppercase rounded disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-[#9C8B73]" data-testid="chat-empty">
            Pick a thread on the left.
          </div>
        )}
      </section>
    </div>
  );
}
